var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Box, Divider } from '@mui/material';
import React, { Fragment, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useState, } from 'react';
import { useElementSizeThrottled } from '../../utils/hooks';
import Select from '../Select/Select';
import SelectFormInput from '../SelectFormInput/SelectFormInput';
import ElementsWrapper from './ElementsWrapper';
import { MENU_ID, OverflowMenu } from './OverflowMenu';
import { Alignment, Visibility, } from './_definitions/ActionGroupDefinitions';
import { getElement, getStyleValue } from './_helpers/utils';
import { TransformToMenuItem } from './_helpers/TransformToMenuItem';
import { rem } from '../../utils/rem';
import { resolveSxAsArray } from '../../utils/utils';
// Constants.
var DEFAULT_CMP_HEIGHT = 30;
var ACTION_GROUP_CMP_HEIGHT = DEFAULT_CMP_HEIGHT + 2 + 4; // + 2 for the border and + 4 for the paddings (top, bottom)
// Styles.
var styles = {
    root: function (props) { return (__assign(__assign({ alignItems: 'center', display: 'flex', flexWrap: 'nowrap', gap: 1, minHeight: rem(ACTION_GROUP_CMP_HEIGHT), overflow: 'hidden', padding: rem(2), margin: rem(-2) }, (props.alignContent === Alignment.RIGHT && { justifyContent: 'flex-end' })), (props.overrideStyles && {
        '& .MuiButton-root': {
            flexShrink: 0,
        },
        '& .MuiFormControl-root': {
            '& .MuiBox-root': {
                // Adjust the label so that it won't be wrapped and the margins are more suitable for the action group.
                flexShrink: 0,
                ml: 0,
                mr: 1,
            },
            '& .MuiFormHelperText-root': {
                // Hide the helper text so that it won't corrupt the design. The hint/error is not needed there anyway.
                display: 'none',
            },
        },
        '& .MuiFormControlLabel-root': {
            flexShrink: 0,
            height: rem(DEFAULT_CMP_HEIGHT),
            m: 0,
            '& .MuiTypography-root': {
                ml: rem(11),
            },
        },
        '& .MuiCheckbox-root': {
            alignSelf: 'center',
            m: 0,
        },
        '& .MuiSwitch-root': {
            height: rem(DEFAULT_CMP_HEIGHT),
            m: 0,
        },
        '& .MuiDivider-root': {
            alignSelf: 'center',
            height: rem(DEFAULT_CMP_HEIGHT),
        },
    }))); },
};
var ActionGroupInner = function (_a) {
    var agRef = _a.agRef, _b = _a.alignContent, alignContent = _b === void 0 ? (Alignment === null || Alignment === void 0 ? void 0 : Alignment.LEFT) || 'left' : _b, _c = _a.hideFirstPaint, hideFirstPaint = _c === void 0 ? false : _c, items = _a.items, MenuProps = _a.MenuProps, _d = _a.overrideStyles, overrideStyles = _d === void 0 ? true : _d, sx = _a.sx, props = __rest(_a, ["agRef", "alignContent", "hideFirstPaint", "items", "MenuProps", "overrideStyles", "sx"]);
    var _e = useState(0), menuButtonWidth = _e[0], setMenuButtonWidth = _e[1];
    var measuredRef = useCallback(function (node) {
        if (node !== null) {
            setMenuButtonWidth(node.getBoundingClientRect().width);
        }
    }, []);
    var _f = useElementSizeThrottled(25), rootRef = _f.ref, width = _f.width;
    // Flag for checking the font loading status. This is needed for calculating the min. size of the elements.
    // If fonts are not loaded, the min. size will be calculated incorrectly.
    // If fonts are loaded initially (in the first layoutEffect), this state will not be changed so it will not trigger a re-render!
    var _g = useState(false), fontsLoaded = _g[0], setFontsLoaded = _g[1];
    // Hook for storing the min. sizes of all the visible elements.
    var _h = useState([]), minSizes = _h[0], setMinSizes = _h[1];
    // Hook for storing the highest element index. This is needed for calculating how many items with no visibility set can be displayed.
    var _j = useState(items.length), highestIndex = _j[0], setHighestIndex = _j[1];
    // Hook for storing the gap size between elements.
    var _k = useState(8), gap = _k[0], setGap = _k[1];
    // Hook for storing an anchor element for the dropdown menu.
    var _l = useState(null), anchorEl = _l[0], setAnchorEl = _l[1];
    var _m = useState(false), showElements = _m[0], setShowElements = _m[1];
    // Create imperative API for the menu.
    // We will provide close method to close the overflow menu (set anchor el to null)
    useImperativeHandle(agRef, function () { return ({
        closeMenu: function () {
            setAnchorEl(null);
        },
    }); }, []);
    /**
     * This `useEffect` is for validating the items, i.e. their structure, supported element types, etc.
     * Dependencies: when the items' count changes.
     */
    // useEffect(() => {
    // validateItems(items)
    // }, [items.length])
    /**
     * Computes the min. size of the specified element. For elements considered as shrinkable it uses a specific size,
     * for other elements it takes their preferred width as the min.
     *
     * @param el The HTML element to get its (min) width from.
     * @param item The wrapper containing an element to check.
     * @returns The computed element size.
     */
    var computeElementSize = function (el, item) {
        var _a, _b;
        // avg. time: 0.5 - 0.01ms
        var size;
        // Check if el is HTMLElement or not.
        if (el instanceof HTMLElement === false) {
            console.warn('[YDS-REACT] - ActionGroup: Element is not an instance of HTMLElement! Element:', el, 'Item: ', item);
            return 0;
        }
        var nodeStyle = window.getComputedStyle(el);
        var elType = getElement(item.el);
        if (elType === null || elType === void 0 ? void 0 : elType.isShrinkable) {
            // Compute the shrinkable component's min. width.
            // Either get the one defined by user or by us.
            size = getStyleValue(nodeStyle, 'min-width') || elType.minWidth || 0;
            // In case of form fields we have to add also the width of their label.
            if (elType.isFormField) {
                var elWidth = getStyleValue(nodeStyle, 'width');
                if (el.childNodes[1] instanceof HTMLElement === false) {
                    console.warn('[YDS-REACT] - ActionGroup: Element is not an instance of HTMLElement! Element:', el.childNodes[1]);
                    return 0;
                }
                var inputStyle = window.getComputedStyle(el.childNodes[1]);
                size += elWidth - getStyleValue(inputStyle, 'width');
            }
            ;
            el.style.setProperty('min-width', "".concat(size.toString(), "px"));
        }
        else {
            size =
                el.getBoundingClientRect().width +
                    getStyleValue(nodeStyle, 'margin-right') +
                    getStyleValue(nodeStyle, 'margin-left');
            // Special case - although Select box might be a shrinkable input, we do not allow it as changing
            // the options could change its width which is undesirable behaviour from the UX perspective.
            if (((_a = item.el) === null || _a === void 0 ? void 0 : _a.type) === SelectFormInput || ((_b = item.el) === null || _b === void 0 ? void 0 : _b.type) === Select) {
                ;
                el.style.setProperty('flex-shrink', '0');
            }
        }
        // +1.5 px to prevent edge case where last Element is is partly hidden.
        return size + 1.5;
    };
    var itemsVisibilitiesJSON = useMemo(function () { return JSON.stringify(items.map(function (item) { return item.visibility; })); }, [items]);
    /**
     * This `useEffect` is for storing the sizes of all visible elements.
     * Dependencies: when either the items' count and/or the gap changes.
     */
    useLayoutEffect(function () {
        var _a, _b;
        // If font are loaded measuring will continue. Otherwise it will be skipped and the effect will be called again.
        if (document.fonts.status !== 'loaded') {
            document.fonts.ready.then(function () {
                setFontsLoaded(true);
                return;
            });
        }
        var nodeStyle = window.getComputedStyle(rootRef === null || rootRef === void 0 ? void 0 : rootRef.current);
        setGap(getStyleValue(nodeStyle, 'gap'));
        var allVisibleElements = Array.from((_b = (_a = rootRef === null || rootRef === void 0 ? void 0 : rootRef.current) === null || _a === void 0 ? void 0 : _a.childNodes) !== null && _b !== void 0 ? _b : []);
        // TODO investigate if this is still valid.
        if (!allVisibleElements.length) {
            // console.log('SAFETY CHECK! Elements are empty!')
            return;
        }
        // Remove the menu button, if any.
        var menuPos = Number(MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.position);
        if (menuPos > 0 &&
            menuPos < items.length &&
            allVisibleElements[menuPos - 1].id === MENU_ID) {
            allVisibleElements.splice(menuPos - 1, 1);
        }
        // Compute the sizes.
        var sizes = new Array(items.length);
        var j = 0; // Represents an index of the displayed/visible elements.
        for (var i = 0; i < items.length; i += 1) {
            var size = 0;
            // If the element is visible, compute its size. Otherwise, use the min. size that was computed before.
            // By using LayoutEffect we are sure that minsSizes are computed because all elements are in the DOM (but may not be painted yet)
            if (items[i].visibility !== Visibility.HIDDEN &&
                allVisibleElements[j] instanceof HTMLElement !== false) {
                size = computeElementSize(allVisibleElements[j], items[i]);
                j += 1;
            }
            sizes[i] = size || minSizes[i];
        }
        setMinSizes(sizes);
    }, [items.length, fontsLoaded]);
    /**
     * Returns a sum of min. sizes of all always visible elements (incl. the gap).
     *
     * @returns A sum of min. sizes of all always visible elements.
     */
    var getAlwaysVisibleElementsTotalMinSize = function () {
        var totalSize = 0;
        if ((minSizes === null || minSizes === void 0 ? void 0 : minSizes.length) && gap != null) {
            for (var i = 0; i < items.length; i += 1) {
                if (items[i].visibility === Visibility.ALWAYS) {
                    totalSize += minSizes[i] || 30 + gap;
                }
            }
        }
        return totalSize;
    };
    /**
     * This `useEffect` is for calculating how many elements can fit (be displayed) into the root container by storing
     * the highest index from the list of items. It is triggered every time the width of the root container changes.
     * Dependencies: when either the width and/or the min. sizes changes.
     */
    useEffect(function () {
        if (!width || !(minSizes === null || minSizes === void 0 ? void 0 : minSizes.length) || gap == null) {
            return;
        }
        var sum = 0;
        var index = -1;
        // Calculate the available space excluding the always visible elements and the overflow menu (incl. gaps).
        // The rem(8px) is for the padding and margin of the root container.
        var availableSpace = width - 8 - getAlwaysVisibleElementsTotalMinSize() - menuButtonWidth;
        for (var i = 0; i < items.length; i += 1) {
            if (items[i].visibility == null) {
                // Stop if it does not fit (incl. the gap).
                if (sum + minSizes[i] + gap > availableSpace) {
                    break;
                }
                sum += minSizes[i] + gap;
                index = i;
            }
        }
        // Avoid unnecessary redraws.
        if (index !== highestIndex) {
            setHighestIndex(index);
        }
    }, [width, minSizes, items.length, itemsVisibilitiesJSON, menuButtonWidth]);
    // closes the menu
    var handleClose = useCallback(function () {
        setAnchorEl(null);
    }, []);
    /**
     * Returns a list of indices indicating which elements should be displayed based on their visibility and/or
     * the calculated highest index.
     *
     * @returns A list of indices.
     */
    var visibleElementsIndices = useMemo(function () {
        var indices = [];
        for (var i = 0; i < items.length; i += 1) {
            if (items[i].visibility !== Visibility.HIDDEN &&
                (items[i].visibility === Visibility.ALWAYS || i <= highestIndex)) {
                indices.push(i);
            }
        }
        return indices;
    }, [highestIndex, itemsVisibilitiesJSON]);
    /**
     * This memo will calculate hidden and displayed elements based on the list of items and the list of visible elements.
     * hidden: Contains a lists of transformed elements to hide into the dropdown menu.
     * displayed: Contains a list of transformed elements to display in the root container.
     */
    var elements = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        var hidden = [];
        var displayed = [];
        var options = {
            handleClose: handleClose,
        };
        for (var i = 0; i < items.length; i += 1) {
            var item = items[i];
            var key = ((_a = item.el) === null || _a === void 0 ? void 0 : _a.key) || (item === null || item === void 0 ? void 0 : item.label) || ((_c = (_b = item.el) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.label) || 'ag-item-' + i;
            if (!visibleElementsIndices.includes(i)) {
                var newItem = (React.createElement(TransformToMenuItem, { key: key, item: item, options: options, index: i }));
                if (newItem) {
                    hidden.push(newItem);
                }
            }
            else {
                displayed.push(React.createElement(Fragment, { key: key }, item.el));
            }
        }
        // If item is divider and is first or last in menu remove divider from list.
        if (((_g = (_f = (_e = (_d = hidden[0]) === null || _d === void 0 ? void 0 : _d.props) === null || _e === void 0 ? void 0 : _e.item) === null || _f === void 0 ? void 0 : _f.el) === null || _g === void 0 ? void 0 : _g.type) === Divider) {
            hidden.shift();
        }
        if (((_l = (_k = (_j = (_h = hidden[hidden.length - 1]) === null || _h === void 0 ? void 0 : _h.props) === null || _j === void 0 ? void 0 : _j.item) === null || _k === void 0 ? void 0 : _k.el) === null || _l === void 0 ? void 0 : _l.type) === Divider) {
            hidden.pop();
        }
        if ((minSizes === null || minSizes === void 0 ? void 0 : minSizes.length) !== 0) {
            // This will prevent flickering of the elements.
            window.requestAnimationFrame(function () {
                setShowElements(true);
            });
        }
        return { displayed: displayed, hidden: hidden };
    }, [visibleElementsIndices, items]);
    var memoStyles = useMemo(function () { return __spreadArray([
        styles.root({ alignContent: alignContent, overrideStyles: overrideStyles, hideFirstPaint: hideFirstPaint })
    ], resolveSxAsArray(sx), true); }, [alignContent, overrideStyles, hideFirstPaint, sx]);
    var rootStyle = hideFirstPaint && !showElements
        ? {
            opacity: '0',
        }
        : {};
    return (React.createElement(Box, __assign({ ref: rootRef, sx: memoStyles }, props, { "data-component": "action-group-container", style: rootStyle }),
        React.createElement(ElementsWrapper, { displayedElements: elements.displayed, menuPropsPosition: MenuProps === null || MenuProps === void 0 ? void 0 : MenuProps.position, displayOverflowMenu: elements.hidden.length > 0, items: items },
            React.createElement(OverflowMenu, { hiddenElements: elements.hidden, MenuProps: MenuProps, ref: measuredRef, setAnchorEl: setAnchorEl, anchorEl: anchorEl }))));
};
/**
 * The `ActionGroup` is a responsive container for creating tool- or action-bars that can seamlessly adapt to given width.
 * It does not contain any background or frame, instead it is usually wrapped in another container like toolbar, frame etc.
 *
 * Typical usage:
 * - Detail view -> action bar
 * - List -> action bar
 * - List item -> context actions
 * - Tool bar (docked / floating)
 *
 * Alignment:
 * - content can be left-aligned or right-aligned.
 *
 * Supported elements:
 * - see `Element` constant.
 *
 * How it works under the hood:
 * - the main idea is to store the min. sizes of all visible elements on start-up and then calculate which of them can fit
 *   into the root container every time its width changes.
 *
 * @returns A container with the specified elements which can fit into it.
 */
var ActionGroup = function (props) { return (
// Wrapper around ActionGroup that will be exported instead of action group and will send all of the props and ref to the action group
// By using key, we wil force the component to rerender when the items are added or removed. Thanks to this new sizes can be computed.
React.createElement(ActionGroupInner, __assign({}, props, { key: props.items.length }))); };
export default ActionGroup;
